// Organization create/edit modal component

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Checkbox,
  CheckboxGroup,
  FormHelperText,
} from '@chakra-ui/react';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CountryCodeSelect } from '@/design-system/components/CountryCodeSelect';
import { FormField } from '@/components/Modal/FormField';
import type { FeatureAccess } from '@/lib/sdk/organization/types';
import { timeZones } from '@/utility/timeZones';

const TIME_ZONE_OPTIONS = timeZones.map((zone) => ({ value: zone, label: zone.replace(/_/g, ' ') }));

// Day starts fall on 15-minute steps, so every UTC offset in use lands on a sweep run.
const DAY_START_OPTIONS = Array.from({ length: 96 }, (_, step) => {
  const time = `${String(Math.floor(step / 4)).padStart(2, '0')}:${String((step % 4) * 15).padStart(2, '0')}`;
  return { value: time, label: time };
});

// Single source of truth for both the rendered checkboxes and the whitelist
// used to sanitize a fetched org's feature_access below. Org rows can carry
// legacy values (e.g. the retired "stores"/"events" org_features enum
// members — the Postgres enum type itself was never pruned when those
// features were dropped, only the OpenAPI/API-layer allow-list was) that
// this modal has no checkbox for; submitting them back unfiltered 400s on
// PATCH since UpdateOrgRequest validates feature_access against the current
// (narrower) enum — breaking every edit to that org, not just feature
// toggles.
const FEATURE_ACCESS_OPTIONS: { value: FeatureAccess; label: string }[] = [
  { value: 'reports', label: 'Reports' },
  { value: 'team', label: 'Team' },
  { value: 'workflows', label: 'Workflows' },
  { value: 'master_data', label: 'Master Data' },
  { value: 'ai_chat', label: 'AI Workflow Assistant' },
  { value: 'nudges', label: 'Reminders' },
];
const VALID_FEATURE_ACCESS = new Set<string>(FEATURE_ACCESS_OPTIONS.map((o) => o.value));

const organizationSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  country_code: z.string().min(1, 'Country code is required'),
  phone_number: z.string().min(1, 'Phone number is required'),
  default_country_code: z.string().regex(/^\+[0-9]{1,4}$/, 'Enter + followed by 1 to 4 digits'),
  time_zone: z.string().min(1, 'Time zone is required'),
  day_start: z.string().regex(/^\d{2}:(00|15|30|45)$/, 'Choose when the business day starts'),
  dashboard_access: z.boolean().default(false),
  feature_access: z.array(z.string()).default(['reports']),
  demo_mode: z.boolean().default(false),
  demo_otp: z.number().int().min(100000).max(999999).optional(),
});

export type OrganizationFormData = z.infer<typeof organizationSchema>;

interface OrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: OrganizationFormData) => void;
  initialData?: OrganizationFormData;
  isEditing?: boolean;
}

/**
 * Modal for creating or editing organizations
 * Used on OrgsPage for super admin users
 */
export function OrganizationModal({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isEditing = false,
}: OrganizationModalProps) {
  const sanitizedInitialData = useMemo(() => {
    if (!initialData) return initialData;
    return {
      ...initialData,
      feature_access: initialData.feature_access.filter((v) => VALID_FEATURE_ACCESS.has(v)),
    };
  }, [initialData]);

  // A zone set through the API may not be in the list; offer it, or saving the form would
  // quietly switch the org to the first zone listed.
  const timeZoneOptions = useMemo(() => {
    const zone = sanitizedInitialData?.time_zone;
    return zone && !timeZones.includes(zone) ? [{ value: zone, label: zone }, ...TIME_ZONE_OPTIONS] : TIME_ZONE_OPTIONS;
  }, [sanitizedInitialData]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
    setValue,
  } = useForm<z.input<typeof organizationSchema>, unknown, OrganizationFormData>({
    resolver: zodResolver(organizationSchema),
    defaultValues: sanitizedInitialData || {
      name: '',
      email: '',
      phone_number: '',
      country_code: '+91',
      default_country_code: '+91',
      time_zone: 'Asia/Calcutta',
      day_start: '',
      dashboard_access: false,
      feature_access: ['reports'],
      demo_mode: false,
      demo_otp: undefined,
    },
    values: sanitizedInitialData,
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" closeOnOverlayClick={false}>
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent>
        <form onSubmit={handleSubmit(onSubmit, (errors) => console.error('Form validation errors:', errors))}>
          <ModalHeader>
            {isEditing ? 'Edit Organization' : 'Add Organization'}
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody>
            <VStack spacing={4} align="stretch">
              {/* Organization Name */}
              <FormField
                label="Organization Name"
                name="name"
                register={register}
                error={errors.name}
                placeholder="Enter organization name"
                required
              />

              {/* Email */}
              <FormField
                label="Email"
                name="email"
                register={register}
                error={errors.email}
                type="email"
                placeholder="Enter email address"
                required
              />

              {/* Country Code */}
              <FormControl isInvalid={!!errors.country_code} isRequired>
                <FormLabel>Country Code</FormLabel>
                <CountryCodeSelect
                  value={watch('country_code')}
                  onChange={(value) => setValue('country_code', value)}
                  error={errors.country_code?.message}
                />
              </FormControl>

              {/* Phone Number */}
              <FormField
                label="Phone Number"
                name="phone_number"
                register={register}
                error={errors.phone_number}
                type="tel"
                placeholder="Enter phone number"
                required
              />

              {/* Default Country Code */}
              <FormControl isInvalid={!!errors.default_country_code} isRequired>
                <FormLabel>Default Country Code</FormLabel>
                <CountryCodeSelect
                  value={watch('default_country_code')}
                  onChange={(value) => setValue('default_country_code', value)}
                  error={errors.default_country_code?.message}
                  testId="org-form-default-country-code"
                />
                <FormHelperText fontSize="sm" color="gray.600">
                  Added to a team member's mobile entered without a country code.
                </FormHelperText>
              </FormControl>

              {/* Business day */}
              <FormField
                label="Time Zone"
                name="time_zone"
                type="select"
                options={timeZoneOptions}
                placeholder="Select time zone"
                register={register}
                error={errors.time_zone}
                required
                testId="org-form-time-zone"
              />
              <FormField
                label="Day Start"
                name="day_start"
                type="select"
                options={DAY_START_OPTIONS}
                placeholder="Select day start"
                register={register}
                error={errors.day_start}
                required
                description={isEditing
                  ? 'Changing the time zone or day start moves the deadlines of live daily, weekly and monthly checklists once.'
                  : 'The time of day when no shift is running. Daily checklists expire and new ones start at this time.'}
                testId="org-form-day-start"
              />

              {/* Dashboard Access Checkbox */}
              <FormControl>
                <Checkbox
                  {...register('dashboard_access')}
                  colorScheme="brand"
                >
                  Enable Client Portal Access
                </Checkbox>
                <FormHelperText fontSize="sm" color="gray.600">
                  Allow organization to access the client portal
                </FormHelperText>
              </FormControl>

              {/* Demo Mode Checkbox */}
              <FormControl>
                <Checkbox
                  {...register('demo_mode')}
                  colorScheme="orange"
                >
                  Enable Demo Login
                </Checkbox>
                <FormHelperText fontSize="sm" color="gray.600">
                  Allow login with a fixed OTP code (staging/demo environments only)
                </FormHelperText>
              </FormControl>

              {/* Demo OTP — shown only when demo_mode is enabled */}
              {watch('demo_mode') && (
                <FormField
                  label="Demo OTP Code"
                  name="demo_otp"
                  register={register}
                  error={errors.demo_otp}
                  type="number"
                  placeholder="Enter 6-digit demo code (e.g. 123456)"
                  min={100000}
                  max={999999}
                  required
                />
              )}

              {/* Feature Access Checkbox Group */}
              {watch('dashboard_access') && (
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="medium">
                    Available Features
                  </FormLabel>
                  <CheckboxGroup
                    value={watch('feature_access')}
                    onChange={(values) => setValue('feature_access', values as FeatureAccess[])}
                  >
                    <VStack align="start" spacing={2}>
                      {FEATURE_ACCESS_OPTIONS.map((option) => (
                        <Checkbox key={option.value} value={option.value} colorScheme="brand" data-testid={`org-feature-access-${option.value}`}>
                          {option.label}
                        </Checkbox>
                      ))}
                    </VStack>
                  </CheckboxGroup>
                  <FormHelperText fontSize="sm" color="gray.600">
                    Select which features this organization can access
                  </FormHelperText>
                </FormControl>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter gap={3}>
            <Button
              variant="ghost"
              onClick={handleClose}
              isDisabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="brand"
              isLoading={isSubmitting}
              loadingText="Submitting"
              data-testid="org-modal-submit-btn"
            >
              {isEditing ? 'Update' : 'Add'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
